import { useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { useShipmentNotifications } from "~/features/shipment-management/hooks/use-shipment-notifications.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { deleteAndValidate } from "~/shared/services/api/fetch-client.ts";

export const restoreShipment = async (poNumber: number) => {
	const url = apiRoutes.POSTVOIDUNVOIDSHIPMENT.replace(
		":poNumber",
		poNumber.toString(),
	);
	const resp = await deleteAndValidate(url, z.unknown());

	if (resp.success) {
		return resp.data;
	}

	throw new Error(resp.error);
};

export const useRestoreShipment = () => {
	const queryClient = useQueryClient();
	const { showSuccess, showError } = useShipmentNotifications();
	const { mutate, mutateAsync, isPending, error, reset } = useMutation<
		unknown,
		Error,
		number
	>({
		mutationKey: ["restoreShipment"],
		mutationFn: (poNumber: number) => restoreShipment(poNumber),
		onSuccess: async () => {
			// Await the refetch so the mutation stays pending (and the spinner
			// stays visible) until the shipment data has finished reloading.
			await queryClient.invalidateQueries({ queryKey: ["shipment"] });
			showSuccess("Shipment restored.");
		},
		onError: (err) => {
			showError(
				err instanceof Error
					? err.message
					: "Failed to restore shipment. Please try again.",
			);
		},
	});

	return {
		restoreShipment: mutate,
		restoreShipmentAsync: mutateAsync,
		isRestoringShipment: isPending,
		error,
		resetError: reset,
	};
};
